import React from 'react';
import { Pressable, StyleSheet } from 'react-native';

import { DesktopSearchIcon } from '@/assets/icons/desktopSearch';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useHover } from '@/feature/responsive-design/hooks/use-hover';
import { isWeb } from '@/utils/constants-platform-specific';

const isMac = isWeb && typeof navigator !== 'undefined' && /Mac/i.test(navigator.userAgent);

type DesktopSearchButtonProps = {
    onPress: () => void;
    accessibilityLabel?: string;
    testID?: string;
};

/**
 * Search icon button with the ⌘K hotkey hint of the desktop web header
 */
export const DesktopSearchButton = ({ onPress, accessibilityLabel, testID }: DesktopSearchButtonProps) => {
    const { hovered, hoverHandlers } = useHover();

    return (
        <Row alignItems="center" gap="s8">
            <Pressable
                {...hoverHandlers}
                onPress={onPress}
                style={[styles.button, hovered ? styles.buttonHovered : null]}
                accessibilityRole="button"
                accessibilityLabel={accessibilityLabel}
                testID={testID}
            >
                <DesktopSearchIcon color={hovered ? ds.colorValue.icon.primary : undefined} />
            </Pressable>
            <Box style={styles.hotkey}>
                <Text variant="labelMedium" color={hovered ? ds.colorToken.text.primary : ds.colorToken.text.secondary}>
                    {isMac ? '⌘K' : 'Ctrl K'}
                </Text>
            </Box>
        </Row>
    );
};

const styles = StyleSheet.create({
    button: {
        borderColor: 'transparent',
        borderRadius: 9999,
        borderWidth: 1,
        padding: 7,
    },
    buttonHovered: {
        backgroundColor: ds.colorValue.background.neutral.subtle,
        borderColor: ds.colorValue.border.subtle,
    },
    hotkey: {
        backgroundColor: ds.colorValue.background.neutral.subtler,
        borderRadius: 6,
        paddingHorizontal: 6,
        paddingVertical: 2,
    },
});
